<template>
    <div class="srv-app">
        <!-- Bandeau hors ligne. Placé AVANT l'en-tête et non par-dessus : dans la
             maquette Stitch il recouvrait le « Bonjour, Jean ». -->
        <div v-if="horsLigne" class="srv-offline">
            <i class="ri-wifi-off-line"></i>
            <span>Hors ligne - vos commandes sont enregistrées sur cet appareil.</span>
        </div>

        <!-- Voyant temps réel : n'apparaît que si un enrôlement caisse a été
             délivré. Vert = notre socket est up ET la caisse est dans la room ;
             sinon on prévient que seul le QR fait foi. -->
        <div v-if="rt.actif" class="srv-rt" :class="{ 'srv-rt-on': rt.socket && rt.caisse }">
            <i :class="(rt.socket && rt.caisse) ? 'ri-links-line' : 'ri-link-unlink-m'"></i>
            <span v-if="rt.socket && rt.caisse">Caisse connectée - commandes envoyées en direct</span>
            <span v-else>Caisse hors ligne - présentez le QR au caissier</span>
        </div>

        <!-- Plats prêts à servir, poussés par la cuisine / le bar. -->
        <div v-if="platsPrets.length" class="srv-prets">
            <div v-for="p in platsPrets" :key="'pret'+p.bonId" class="srv-pret-item">
                <span class="srv-pret-ico"><i class="ri-restaurant-2-fill"></i></span>
                <div class="srv-pret-info">
                    <strong>Plat prêt à servir</strong>
                    <span>
                        <template v-if="p.tableNom">{{ p.tableNom }} · </template>
                        {{ p.numeroBon || p.docnum }}
                        <em v-if="p.poste === 'bar'"> · Bar</em>
                    </span>
                </div>
                <button class="srv-pret-btn" @click="acquitterPlat(p)">
                    <i class="ri-check-line"></i> Je prends
                </button>
            </div>
        </div>

        <!-- Vérification d'accès : rien n'est monté tant que le serveur (ou le
             cache) n'a pas validé la clé du QR mural. -->
        <div v-if="bootState === 'checking'" class="srv-boot">
            <div class="srv-boot-spin"></div>
            <p class="srv-boot-txt">Ouverture du service…</p>
            <p v-if="seedProgress" class="srv-boot-sub srv-num">
                Téléchargement {{ seedProgress.fait }} / {{ seedProgress.total }}
            </p>
        </div>

        <!-- 'denied' n'affiche rien : la redirection est lancée, monter l'app
             le temps d'un tick la ferait clignoter. -->
        <template v-else-if="bootState !== 'denied'">

        <!-- Aucun événement : l'identité ne se saisit plus, elle est délivrée
             par le QR de la caisse. Cet écran explique seulement la marche à
             suivre au serveur qui arrive sans enrôlement. -->
        <identification v-if="!serveur" />

        <template v-else>
            <accueil
                v-if="vue === 'accueil' || vue === 'commandes'"
                :serveur="serveur"
                :commandes="commandes"
                :historique="vue === 'commandes'"
                @nouvelle="demarrerCommande"
                @ouvrir="ouvrirCommande"
            />

            <choixtable
                v-else-if="vue === 'table'"
                :tables="tables"
                :occupees="tablesOccupees"
                :compteur="nbCommandesTable"
                @choisir="tableChoisie"
                @fermer="vue = 'accueil'"
            />

            <clientstable
                v-else-if="vue === 'clients'"
                :table="tableActive"
                :clients="clientsTableActive"
                @reprendre="reprendreCommande"
                @nouveau="nouvelleCommandeSurTable(tableActive)"
                @fermer="vue = 'table'"
            />

            <lacarte
                v-else-if="vue === 'carte'"
                :carte="carte"
                :table="brouillon.table"
                :lignes="brouillon.lignes"
                @ouvrir-plat="platOuvert = $event"
                @voir-commande="vue = 'recap'"
                @retour="annulerBrouillon"
            />

            <recapitulatif
                v-else-if="vue === 'recap'"
                :brouillon="brouillon"
                :deja-enregistree="!!brouillon.id"
                @ajouter="vue = 'carte'"
                @quantite="changerQuantite"
                @supprimer="supprimerLigne"
                @renommer="renommerClient(brouillon, $event)"
                @enregistrer="enregistrerCommande"
                @passer-commande="passerLaCommande"
                @passer-caisse="passerALaCaisse"
                @retour="vue = 'carte'"
            />

            <template v-else-if="vue === 'qr'">
              <!-- Bannière : ce que la CAISSE a modifié sur cette commande (rupture, retrait…) -->
              <div v-if="evenementsCaisse.length" class="srv-evt-banner" :class="{ nonlu: nbEvenementsNonLus > 0 }">
                <div class="srv-evt-head">
                  <span class="srv-evt-titre">
                    <i class="ri-error-warning-line"></i> Modifications de la caisse
                    <span v-if="nbEvenementsNonLus > 0" class="srv-evt-badge">{{ nbEvenementsNonLus }}</span>
                  </span>
                  <button v-if="nbEvenementsNonLus > 0" class="srv-evt-vu" @click="marquerEvenementsLus">Vu</button>
                </div>
                <ul class="srv-evt-list">
                  <li v-for="e in evenementsCaisse" :key="e.id" :class="{ nonlu: !e.lu_at }">
                    <i :class="e.type === 'retrait' ? 'ri-close-circle-line' : 'ri-edit-line'"></i>
                    <span class="srv-evt-msg">{{ e.message || (e.type + ' - ' + e.article_nom) }}</span>
                    <span v-if="e.motif" class="srv-evt-motif">{{ e.motif }}</span>
                  </li>
                </ul>
                <button class="srv-evt-hist" @click="showHistoriqueEvenements = !showHistoriqueEvenements">
                  <i class="ri-history-line"></i>
                  {{ showHistoriqueEvenements ? 'Masquer l\'historique' : 'Historique complet' }}
                </button>
                <ul v-if="showHistoriqueEvenements" class="srv-evt-list srv-evt-all">
                  <li v-for="e in evenementsActive" :key="'h' + e.id">
                    <span class="srv-evt-acteur" :class="e.acteur">{{ e.acteur }}</span>
                    <span class="srv-evt-msg">{{ e.message || (e.type + ' - ' + e.article_nom) }}</span>
                    <small class="srv-evt-date">{{ e.created_at }}</small>
                  </li>
                </ul>
              </div>

              <qrcommande
                  :commande="commandeActive"
                  :mode="modeQr"
                  :rt-ack="rtAck"
                  @envoye="marquerEnvoye(commandeActive)"
                  @encaissee-par-moi="marquerEncaissee(commandeActive, 'moi')"
                  @a-encaisser="demarrerVerification(commandeActive)"
                  @reverifier="reverifier(commandeActive)"
                  @rouvrir="rouvrirCommande(commandeActive)"
                  @retour="vue = 'accueil'"
              />
            </template>

            <pointdecaisse
                v-else-if="vue === 'caisse'"
                :serveur="serveur"
                :commandes="commandes"
                :fond-de-caisse="fondDeCaisse"
                @cloturer="cloturerService"
            />

            <profil
                v-else-if="vue === 'profil'"
                :serveur="serveur"
                :commandes="commandes"
                @point-de-caisse="vue = 'caisse'"
                @terminer="terminerService"
            />

            <ficheplat
                v-if="platOuvert"
                :item="platOuvert"
                @fermer="platOuvert = null"
                @confirmer="ajouterAuBrouillon"
            />

            <!-- Onglets : masqués pendant la prise de commande, où la barre
                 d'action basse occupe déjà la zone du pouce. -->
            <nav v-if="ongletsVisibles" class="srv-tabs">
                <button v-for="o in onglets" :key="o.cle"
                        class="srv-tab" :class="{ active: ongletActif === o.cle }"
                        @click="allerOnglet(o.cle)">
                    <i :class="o.icone"></i>
                    <span>{{ o.libelle }}</span>
                </button>
            </nav>
        </template>
        </template>
    </div>
</template>

<script>
    /* eslint-disable */

    // Accès et catalogue : mêmes briques que /menu et /panier. Le module
    // serveur s'ouvre en scannant l'affiche murale du restaurant, il dispose
    // donc lui aussi de soc/mag/key et passe par /verifyaccess.
    import {
        getOrCreateDeviceId,
        getSession, isSessionFresh, saveSession, clearSession,
        getCatalogueMeta, touchCatalogueMeta, saveCatalogue,
        applyCatalogueDelta, verifyCatalogueIntegrity, clearCatalogue,
        getAllArticles, verifierAccesScanGo,
        chargerTablesScanGo, saveTables, getTables,
        chargerIngredientsScanGo, saveIngredients, getIngredients,
        statutEncaissementScanGo, statutCommandeScanGo,
        evenementsCommandeScanGo, marquerLuEvenementScanGo,
        sauvegarderStoreServeur, chargerStoreServeur,
    } from "@/service/hsigscangoIndexdb";

    // Persistance propre au serveur : identité et commandes.
    import {
        saveIdentite, getIdentite, clearIdentite,
        saveCommande, getCommandes, clearCommandes, purgerScopesObsoletes,
        prochainNumeroAttente, exporterStore, importerStore,
    } from "@/service/serveurStoreDB";

    import { catalogueVersCarte } from "../menu/mapping";
    import { STATUTS, estEnCours, aDelta, payloadCommande, identifiantCommande, estVerrouillee, codeIngredients } from "./statuts";
    import { FOND_DE_CAISSE, prochainLineId } from "./mocks";
    import { ScangoSocket } from "@/service/scangosocket";

    import identification from "./identification.vue";
    import accueil        from "./accueil.vue";
    import choixtable     from "./choixtable.vue";
    import clientstable   from "./clientstable.vue";
    import lacarte        from "./lacarte.vue";
    import ficheplat      from "./ficheplat.vue";
    import recapitulatif  from "./recapitulatif.vue";
    import qrcommande     from "./qrcommande.vue";
    import pointdecaisse  from "./pointdecaisse.vue";
    import profil         from "./profil.vue";

    export default {
        name: "serveurapp",
        components: {
            identification, accueil, choixtable, clientstable, lacarte,
            ficheplat, recapitulatif, qrcommande, pointdecaisse, profil,
        },
        data: () => ({
            serveur: null,
            vue: "accueil",
            horsLigne: false,

            carte: [],
            tables: [],              // chargées depuis la base à la prise de service
            // true = établissement sans table (comptoir / à emporter) : on saute
            // le choix de table et on va droit à la carte. Déterminé par le
            // chargement des tables, pas supposé.
            modeSansTable: false,
            commandes: [],
            fondDeCaisse: FOND_DE_CAISSE,

            /* -------- accès & catalogue (cf. bootstrap) -------- */
            socParam: "", magParam: "", keyParam: "",
            bootState: "checking",   // checking | ready | offline | denied
            deviceId: "",
            sessionMeta: null,
            catalogueMeta: null,
            seedProgress: null,
            abortCtrl: null,
            demonte: false,

            brouillon: null,        // commande en cours de saisie
            commandeActive: null,   // commande affichée en QR
            modeQr: "paiement",     // "service" (envoi cuisine) | "paiement"
            tableActive: null,      // table dont on liste les clients (vue 'clients')
            platOuvert: null,

            /* Journal d'événements de la commande affichée (retraits caisse,
               modifs…). Chargé à l'ouverture et rafraîchi par le poll/WS. */
            evenementsActive: [],
            showHistoriqueEvenements: false,

            /* Temps réel (WebSocket) - BONUS au-dessus du QR. `actif` : un
               enrôlement temps réel a été délivré (room+token). `caisse` : notre
               socket est connecté ET la caisse est présente dans la room. Sans
               tout ça, le serveur reste en mode QR, sans rien perdre. */
            rt: { actif: false, socket: false, caisse: false, room: "", token: "", pidcaisse: "" },
            /* Plats prêts à servir, poussés par la cuisine/le bar. Chaque entrée :
               { bonId, numeroBon, docnum, tableNom, poste }. Le serveur acquitte
               (« Je prends ») → vu_at côté backend, retrait de la liste. */
            platsPrets: [],
            // Dernier accusé reçu, pour l'afficher sur le QR : { id, statut }.
            rtAck: null,
        }),
        computed: {
            /** Événements « caisse » de la commande affichée (retraits, rupture…). */
            evenementsCaisse() {
                return (this.evenementsActive || []).filter((e) => e.acteur === "caisse");
            },
            /** Nombre d'événements caisse non-lus (badge / bannière). */
            nbEvenementsNonLus() {
                return this.evenementsCaisse.filter((e) => !e.lu_at).length;
            },

            // Le premier onglet est le point d'entrée : « Tables » quand il y en
            // a, « Accueil » sinon (le mot « Tables » n'aurait aucun sens en
            // service au comptoir).
            onglets() {
                return [
                    { cle: "accueil",   libelle: this.modeSansTable ? "Accueil" : "Tables", icone: "ri-layout-grid-line" },
                    { cle: "commandes", libelle: "Commandes", icone: "ri-file-list-3-line" },
                    { cle: "carte",     libelle: "Menu",      icone: "ri-restaurant-line" },
                    { cle: "profil",    libelle: "Profil",    icone: "ri-user-line" },
                ];
            },
            // Pendant la saisie, la barre d'action basse prend la place.
            ongletsVisibles() {
                return ["accueil", "commandes", "profil", "caisse"].indexOf(this.vue) !== -1;
            },
            ongletActif() {
                if (this.vue === "caisse") return "profil";
                return this.vue;
            },
            // Les commandes en cours de la table dont on liste les clients.
            clientsTableActive() {
                if (!this.tableActive) return [];
                return this.commandesActivesTable(this.tableActive);
            },
            // Nombre de commandes (clients) en cours par table - pour l'afficher
            // sur l'écran de choix : une table peut porter plusieurs additions.
            nbCommandesTable() {
                const m = {};
                for (const c of this.commandes) {
                    if (estEnCours(c.statut) && c.table) {
                        m[c.table.pid] = (m[c.table.pid] || 0) + 1;
                    }
                }
                return m;
            },
            // Une table est occupée tant que sa commande n'est pas réglée :
            // ouverte comme en attente de scan.
            tablesOccupees() {
                const m = {};
                for (const c of this.commandes) {
                    if (estEnCours(c.statut) && c.table) m[c.table.pid] = true;
                }
                return m;
            },
        },
        methods: {
            /* ------------------------------------------------ identité */

            /**
             * Récupère l'identité du serveur en poste.
             *
             * Deux sources, dans cet ordre :
             *
             *  1. L'URL, quand le serveur vient de scanner le QR de la caisse.
             *     Elle porte `c` (pid du collaborateur) et `n` (son nom) - un
             *     enregistrement `cltfrs` réel, pas une saisie libre. C'est ce
             *     qui rend les commandes rattachables aux rapports de CA.
             *  2. Le store local, pour une reprise de service en cours.
             *
             * L'URL prime : si le caissier réenrôle un autre serveur sur le même
             * téléphone, c'est lui qui prend le service.
             */
            async chargerIdentite() {
                const q = this.$route.query || {};
                const pid = String(q.c || "").trim();
                const name = String(q.n || "").trim();

                if (pid) {
                    const identite = { pid, name: name || "Serveur", depuis: Date.now() };
                    /* Enrôlement temps réel embarqué dans le lien de prise de
                       service (r=room, tk=token, pc=caisse). Optionnel : absent,
                       le serveur reste en mode QR. Persisté avec l'identité pour
                       survivre à une reprise de service. */
                    const room = String(q.r || "").trim();
                    const token = String(q.tk || "").trim();
                    const pidcaisse = String(q.pc || "").trim();
                    if (room && token) {
                        identite.rt = { room, token, pidcaisse };
                    }
                    await saveIdentite(identite);
                    // La query a fait son office : on la retire de la barre
                    // d'adresse pour qu'un partage de lien ne transmette pas
                    // l'identité avec la licence.
                    this.$router.replace({
                        path: this.$route.path,
                        query: {},
                    }).catch(() => {});
                    return identite;
                }

                return await getIdentite();
            },

            async terminerService() {
                this.deconnecterTempsReel();
                this.rt.actif = false;
                this.serveur = null;
                await clearIdentite();
                this.vue = "accueil";
            },

            /* ------------------------------------------------ navigation */

            allerOnglet(cle) {
                if (cle === "carte") {
                    // Consulter la carte suppose une table : sans elle, on ne
                    // saurait pas où envoyer ce qui est ajouté.
                    return this.demarrerCommande();
                }
                this.vue = cle;
            },

            /* ------------------------------------------------ commande */

            /**
             * Une commande n'est pas figée à sa création : au restaurant, la
             * table commande en plusieurs fois. `brouillon` désigne donc la
             * commande en cours d'édition - nouvelle (id null) ou reprise
             * (l'objet de la liste lui-même, muté sur place).
             */
            async demarrerCommande() {
                // Sans table : commande indépendante immédiate, droit à la carte.
                if (this.modeSansTable) {
                    const numero = await prochainNumeroAttente({
                        agentId: this.serveur ? this.serveur.pid : "",
                    });
                    this.brouillon = { id: null, numero, table: null, client: null, lignes: [], statut: "ouverte" };
                    this.vue = "carte";
                    return;
                }
                // Avec tables : on choisit d'abord la table. Le numéro d'attente
                // et le brouillon ne sont consommés qu'à la vraie création (un
                // nouveau client), jamais à une simple reprise.
                this.brouillon = null;
                this.vue = "table";
            },

            /** Commandes en cours (non réglées) rattachées à une table. */
            commandesActivesTable(table) {
                if (!table) return [];
                return this.commandes.filter(
                    (c) => estEnCours(c.statut) && c.table && c.table.pid === table.pid
                );
            },

            /** Prochain numéro de client libre sur une table (auto-incrément par table). */
            prochainClientNum(table) {
                let max = 0;
                for (const c of this.commandesActivesTable(table)) {
                    if (c.client) max = Math.max(max, Number(c.client.num) || 0);
                }
                return max + 1;
            },

            /**
             * Ouvre une NOUVELLE addition (client) sur une table.
             *
             * Une même table peut porter plusieurs clients indépendants : chacun
             * reçoit son propre numéro de client, et donc sa propre addition
             * (l'identifiant caisse est déjà par-commande).
             */
            async nouvelleCommandeSurTable(table) {
                const numero = await prochainNumeroAttente({
                    agentId: this.serveur ? this.serveur.pid : "",
                });
                this.brouillon = {
                    id: null, numero, table,
                    client: { num: this.prochainClientNum(table), nom: "" },
                    lignes: [], statut: "ouverte",
                };
                this.tableActive = null;
                this.vue = "carte";
            },

            /** Renomme le client d'une commande (« Dame en rouge »…), num conservé. */
            async renommerClient(commande, nom) {
                if (!commande) return;
                if (!commande.client) commande.client = { num: 1, nom: "" };
                commande.client.nom = String(nom || "").trim();
                await this.persister(commande);
            },

            /**
             * Compat des commandes écrites avant le suivi ligne par ligne.
             *
             * Elles n'ont pas de `qteEnvoyee`. Sans défaut, chaque ligne
             * paraîtrait « neuve » et déclencherait un envoi fantôme en cuisine.
             * On suppose donc qu'une commande déjà sortie de « ouverte » a
             * intégralement été passée (qteEnvoyee = qte) ; une commande encore
             * ouverte reste, elle, entièrement à passer (qteEnvoyee = 0).
             */
            migrerCommande(commande) {
                if (!commande) return commande;
                const dejaSortie = commande.statut && commande.statut !== "ouverte";
                for (const l of commande.lignes || []) {
                    if (l.qteEnvoyee === undefined || l.qteEnvoyee === null) {
                        l.qteEnvoyee = dejaSortie ? (Number(l.qte) || 0) : 0;
                    }
                }
                return commande;
            },

            /** Reprendre une commande ouverte pour y ajouter des articles. */
            reprendreCommande(commande) {
                // Une commande déjà encaissée (caisse ou par moi) est en lecture
                // seule : on ne la remet jamais en édition.
                if (commande && estVerrouillee(commande.statut)) {
                    if (window.toastr) window.toastr.info("Commande déjà encaissée : non modifiable.", "<b></b>");
                    this.commandeActive = commande;
                    this.modeQr = "paiement";
                    this.vue = "qr";
                    return;
                }
                this.brouillon = commande;
                this.vue = "recap";
            },
            /**
             * Retour depuis la carte. Une commande déjà commencée n'est jamais
             * jetée : on repasse par le récapitulatif. Seule une saisie encore
             * vide est abandonnée.
             */
            annulerBrouillon() {
                if (this.brouillon && this.brouillon.lignes.length) {
                    this.vue = "recap";
                    return;
                }
                this.brouillon = null;
                this.vue = "accueil";
            },
            tableChoisie(table) {
                const actives = this.commandesActivesTable(table);
                // Table libre → on ouvre directement le premier client.
                if (!actives.length) return this.nouvelleCommandeSurTable(table);
                // Table occupée → écran « clients de la table » (reprendre / nouveau).
                this.tableActive = table;
                this.vue = "clients";
            },

            /**
             * Point d'écriture UNIQUE des commandes.
             *
             * L'objet est passé tel quel, jamais cloné : `brouillon` référence
             * l'élément de `commandes` lui-même (c'est ce qui rend la reprise
             * transparente), et un clone désynchroniserait la liste du store.
             *
             * Une commande sans id n'est pas encore née : rien à écrire.
             */
            async persister(commande) {
                if (!commande || !commande.id) return;
                try {
                    await saveCommande(this.socParam, this.magParam, commande);
                    // Réplique différée au backend (anti-perte), best-effort.
                    this._planifierSauvegardeBackend();
                } catch (e) {
                    // Écrire en base ne doit jamais faire perdre la saisie en
                    // cours : on signale et on garde l'état en mémoire.
                    console.error("[srv] écriture de la commande:", e);
                    if (window.toastr) window.toastr.error("Commande non enregistrée sur l'appareil.", "<b></b>");
                }
            },

            ajouterAuBrouillon(choix) {
                // Même plat, même portion, même accompagnement, même note, mêmes
                // ingrédients retirés ou doublés → une seule ligne dont la
                // quantité augmente. C'est la règle de la caisse (clé
                // pid--prix--PRIX--accompagnement--ACCPID--ing--SIGNATURE).
                const cle = (l) => [l.pid, l.tarifPid, l.accompagnement, l.note, codeIngredients(l)].join("§");
                const nouvelle = {
                    lineId: prochainLineId(),
                    pid: choix.item.pid,
                    libelle: choix.item.libelle,
                    tarifPid: choix.tarif.pid,
                    tarifLibelle: choix.tarif.libelle,
                    accompagnement: choix.accompagnement || "",
                    note: choix.note || "",
                    // [{ c, name, etat }] : ingrédients retirés ou doublés.
                    ingredients: choix.ingredients || [],
                    pu: choix.tarif.pu,
                    qte: choix.qte,
                    // Rien n'est encore parti en cuisine : cette ligne est
                    // entièrement « neuve » et constituera le prochain envoi.
                    qteEnvoyee: 0,
                };
                const existante = this.brouillon.lignes.find((l) => cle(l) === cle(nouvelle));
                if (existante) existante.qte += choix.qte;
                else this.brouillon.lignes.push(nouvelle);
                this.platOuvert = null;
                this.persister(this.brouillon);
            },
            /**
             * Ajuste la quantité d'une ligne.
             *
             * Le zéro ne se traite pas de la même façon selon que la ligne est
             * déjà partie en cuisine :
             *  - jamais envoyée (qteEnvoyee 0) → suppression franche, la cuisine
             *    n'en a jamais entendu parler ;
             *  - déjà envoyée → on la garde à 0. Le delta signé (−qteEnvoyee)
             *    devient une ANNULATION à transmettre au prochain « Passer la
             *    commande ». La supprimer ferait disparaître l'ordre d'annuler.
             */
            changerQuantite(ligne, delta) {
                const envoyee = Number(ligne.qteEnvoyee) || 0;
                const cible = (Number(ligne.qte) || 0) + delta;
                if (cible <= 0) {
                    if (envoyee > 0) ligne.qte = 0;
                    else return this.supprimerLigne(ligne);
                } else {
                    ligne.qte = cible;
                }
                this.persister(this.brouillon);
            },
            supprimerLigne(ligne) {
                // Un plat déjà en cuisine ne s'efface pas d'un geste : on le met
                // à zéro pour que l'annulation parte, la vraie disparition se
                // fera à l'envoi (les lignes à zéro y sont purgées).
                if ((Number(ligne.qteEnvoyee) || 0) > 0) {
                    ligne.qte = 0;
                    return this.persister(this.brouillon);
                }
                this.brouillon.lignes = this.brouillon.lignes.filter((l) => l.lineId !== ligne.lineId);
                this.persister(this.brouillon);
            },

            /**
             * Donne une identité persistante à un brouillon neuf, SANS changer
             * de vue ni vider le brouillon.
             *
             * C'est le point de naissance commun à « Enregistrer », « Passer la
             * commande » et « Passer à la caisse » : un brouillon sans id n'est
             * pas encore une commande. Idempotent - une commande déjà née est
             * renvoyée telle quelle.
             */
            materialiser(commande) {
                if (!commande.id) {
                    commande.id = "c" + Date.now();
                    commande.creeeLe = Date.now();
                    commande.statut = "ouverte";

                    // Rattachement comptable : même forme que ce que la caisse
                    // écrit dans $.jsonv.collaborateur (handleServerSelect).
                    // C'est ce qui rend la commande visible dans les rapports
                    // de CA par collaborateur - impossible tant que l'identité
                    // était une saisie libre sans pid.
                    commande.collaborateur = this.serveur
                        ? { pid: this.serveur.pid, name: this.serveur.name }
                        : null;

                    this.commandes.unshift(commande);
                }
                return commande;
            },

            /**
             * Enregistre le brouillon sans rien envoyer. La commande garde son
             * statut (ouverte ou en_service) : le serveur la complétera au fil
             * du repas. Retourne la commande.
             */
            async enregistrerCommande() {
                if (!this.brouillon || !this.brouillon.lignes.length) return null;
                const commande = this.materialiser(this.brouillon);
                await this.persister(commande);
                this.brouillon = null;
                this.vue = "accueil";
                return commande;
            },

            /**
             * « Passer la commande » - envoi en cuisine.
             *
             * C'est la nouvelle sortie principale, et elle est RÉPÉTABLE :
             * seuls les articles neufs (delta signé ≠ 0) partent, jamais toute
             * la commande. On produit le QR « service » ; l'avancement de
             * qteEnvoyee n'a lieu qu'une fois le QR effectivement scanné
             * (marquerEnvoye), pour que reprendre la même commande recalcule un
             * delta juste même si un scan a été manqué.
             */
            async passerLaCommande() {
                if (!this.brouillon || !aDelta(this.brouillon)) return;
                const commande = this.materialiser(this.brouillon);
                await this.persister(commande);
                this.commandeActive = commande;
                this.modeQr = "service";
                this.rtAck = null;
                this.vue = "qr";
                this._ouvrirEvenements(commande);
                // Temps réel : la caisse reçoit la fournée sans scan. Le QR reste
                // affiché en secours (si non connecté, seul le QR fait foi).
                this.emettreCommandeTempsReel(commande, "service");
                // Filet : confirme « reçue » même si l'ack WS est manqué ou en mode QR.
                this.planifierPollCommande(commande);
            },

            /**
             * Confirme que la fournée est bien partie en cuisine (QR scanné).
             * On aligne qteEnvoyee sur qte - les ajouts sont désormais « connus »
             * de la cuisine, les annulations appliquées - puis on purge les
             * lignes ramenées à zéro. La commande bascule en_service.
             */
            async marquerEnvoye(commande) {
                if (!commande) return;
                for (const l of commande.lignes) l.qteEnvoyee = Number(l.qte) || 0;
                commande.lignes = commande.lignes.filter((l) => (Number(l.qte) || 0) > 0);
                if (commande.statut === "ouverte") commande.statut = "en_service";
                await this.persister(commande);
                this.brouillon = null;
                this.vue = "accueil";
            },

            /**
             * « Passer à la caisse » - paiement. Le chemin direct est conservé :
             * on peut encaisser une commande sans être passé par la cuisine
             * (petit resto, vente rapide). Produit le QR « paiement ».
             */
            async passerALaCaisse() {
                if (!this.brouillon || !this.brouillon.lignes.length) return;
                const commande = this.materialiser(this.brouillon);
                commande.statut = "attente";
                await this.persister(commande);
                this.brouillon = null;
                this.commandeActive = commande;
                this.modeQr = "paiement";
                this.rtAck = null;
                this.vue = "qr";
                this._ouvrirEvenements(commande);
                this.emettreCommandeTempsReel(commande, "paiement");
                this.planifierPollCommande(commande);
            },

            /**
             * Depuis l'accueil : une commande encore en cours d'édition
             * (ouverte ou en service) se reprend ; celles parties au paiement
             * s'affichent en QR.
             */
            ouvrirCommande(commande) {
                if (commande.statut === "ouverte" || commande.statut === "en_service") {
                    return this.reprendreCommande(commande);
                }
                this.commandeActive = commande;
                this.modeQr = "paiement";
                this.vue = "qr";
                this._ouvrirEvenements(commande);
            },

            /**
             * Le QR de paiement était prématuré : la table veut rajouter. On
             * rend la commande éditable, en revenant à en_service si elle avait
             * déjà tourné en cuisine, sinon à ouverte.
             */
            async rouvrirCommande(commande) {
                if (!commande) return;
                // Verrou : une commande encaissée ne se rouvre pas pour édition.
                if (estVerrouillee(commande.statut)) {
                    if (window.toastr) window.toastr.info("Commande déjà encaissée : non modifiable.", "<b></b>");
                    return;
                }
                const dejaEnvoye = (commande.lignes || []).some((l) => (Number(l.qteEnvoyee) || 0) > 0);
                commande.statut = dejaEnvoye ? "en_service" : "ouverte";
                await this.persister(commande);
                this.reprendreCommande(commande);
            },
            async marquerEncaissee(commande, mode) {
                if (!commande) return;
                commande.statut = mode;
                await this.persister(commande);
            },

            /* ------------------------------- vérification de l'encaissement

               Sans WebSocket, le téléphone ne peut pas être notifié : il POLL.
               Déclenché quand le caissier, après avoir scanné, tape « Commande à
               encaisser » sur le téléphone. Borné à 3 essais pour ne pas charger
               le backend ; réarmable si le caissier a été lent. */

            /** Identifiant stable d'une commande, tel que porté par le QR. */
            identifiantCommande(commande) {
                const pid = commande && commande.collaborateur ? commande.collaborateur.pid : "";
                return String(pid) + "-" + String((commande && commande.id) || "");
            },

            /** Le caissier a scanné : la commande passe en vérification, poll lancé. */
            async demarrerVerification(commande) {
                if (!commande) return;
                commande.statut = "verification";
                commande.verif = { count: 0, epuise: false };
                await this.persister(commande);
                this.vue = "accueil";
                this.planifierPoll(commande);
            },

            /** Relance une vérification épuisée (3 nouveaux essais). */
            async reverifier(commande) {
                if (!commande) return;
                commande.statut = "verification";
                commande.verif = { count: 0, epuise: false };
                await this.persister(commande);
                this.planifierPoll(commande);
            },

            /** Programme le prochain essai selon le compteur déjà consommé. */
            planifierPoll(commande) {
                if (!commande || !commande.verif) return;
                if (!this._pollTimers) this._pollTimers = {};
                const delais = [10000, 10000, 10000];   // ~30 s de fenêtre, 3 essais
                const idx = Number(commande.verif.count) || 0;
                if (idx >= delais.length) {            // 3 essais atteints → on arrête
                    commande.verif.epuise = true;
                    this.persister(commande);
                    return;
                }
                this.annulerTimer(commande.id);
                this._pollTimers[commande.id] = setTimeout(
                    () => this.executerPoll(commande), delais[idx]
                );
            },

            /** Un essai de poll : confirme l'encaissement, ou replanifie / épuise. */
            async executerPoll(commande) {
                if (this.demonte || !commande || commande.statut !== "verification") return;
                commande.verif.count = (Number(commande.verif.count) || 0) + 1;

                const id = this.identifiantCommande(commande);
                let paye = null;
                try {
                    const res = await statutEncaissementScanGo({
                        key: this.keyParam, deviceId: this.deviceId, ids: [id],
                    });
                    if (res.status === "ok") {
                        paye = (res.encaisses || []).find((e) => String(e.srvcmdid) === String(id)) || null;
                    }
                } catch (e) {
                    console.error("[srv] poll encaissement:", e);
                }

                if (paye) {
                    commande.statut = "caisse";
                    commande.encaissement = { docnum: paye.docnum || "", montant: Number(paye.montant) || 0 };
                    delete commande.verif;
                    await this.persister(commande);
                    this.annulerTimer(commande.id);
                    if (window.toastr) window.toastr.success("Encaissement confirmé.", "<b></b>");
                    return;
                }

                await this.persister(commande);
                if (commande.verif.count >= 3) {       // épuisé sans confirmation
                    commande.verif.epuise = true;
                    await this.persister(commande);
                    this.annulerTimer(commande.id);
                    return;
                }
                this.planifierPoll(commande);
            },

            /** Reprend les vérifications non abouties après réouverture de l'appli. */
            reprendreVerifications() {
                for (const c of this.commandes) {
                    if (c.statut === "verification" && c.verif && !c.verif.epuise
                        && (Number(c.verif.count) || 0) < 3) {
                        this.planifierPoll(c);
                    }
                }
            },

            annulerTimer(id) {
                if (this._pollTimers && this._pollTimers[id]) {
                    clearTimeout(this._pollTimers[id]);
                    delete this._pollTimers[id];
                }
            },
            annulerTousTimers() {
                if (!this._pollTimers) return;
                for (const k of Object.keys(this._pollTimers)) clearTimeout(this._pollTimers[k]);
                this._pollTimers = {};
            },

            /**
             * Clôture : c'est ce qui met fin à l'enrôlement.
             *
             * L'identité est effacée en même temps que les commandes - le
             * téléphone redevient vierge et il faudra repasser à la caisse pour
             * reprendre un service. C'est la contrepartie assumée du contrôle
             * par le caissier.
             */
            async cloturerService() {
                this.deconnecterTempsReel();
                this.rt.actif = false;
                // On envoie une dernière photo au backend (au cas où) AVANT de vider,
                // puis on ne vide QUE les commandes de CE serveur (téléphone partagé).
                const pid = this.serveur ? this.serveur.pid : "";
                await this.sauvegarderStoreBackend();
                await clearCommandes(this.socParam, this.magParam, pid);
                await clearIdentite();
                this.commandes = [];
                this.serveur = null;
                if (window.toastr) {
                    window.toastr.success("Service clôturé. Bonne fin de journée !", "<b></b>");
                }
                this.vue = "accueil";
            },

            /* ============================================================
               Démarrage - porté depuis menu/index.vue (bootstrap).
               ============================================================ */

            /**
             * Machine à états du démarrage.
             *
             * Chemin rapide seulement si les TROIS conditions tiennent :
             * session fraîche (borne la révocation à ttlSeconds), même clé qu'à
             * la dernière ouverture (détecte un QR régénéré), catalogue intègre
             * (détecte une écriture IndexedDB interrompue).
             */
            async bootstrap() {
                if (!this.socParam || !this.magParam || !this.keyParam) {
                    return this.refuser("PARAMS_INVALID");
                }

                this.bootState = "checking";
                this.deviceId = await getOrCreateDeviceId();

                const session = await getSession(this.socParam, this.magParam);
                if (session && isSessionFresh(session) && session.key === this.keyParam) {
                    const meta = await getCatalogueMeta(this.socParam, this.magParam);
                    if (meta && await verifyCatalogueIntegrity(this.socParam, this.magParam, meta)) {
                        this.sessionMeta = session;
                        this.catalogueMeta = meta;
                        await this.demarrerService("ready");
                        // Aucun appel de contrôle : tant que la session est
                        // fraîche, le serveur répondrait « rien n'a changé ».
                        return;
                    }
                }

                await this.verifierEtCharger(false);
            },

            async verifierEtCharger(forcerFull) {
                const soc = this.socParam, mag = this.magParam;
                const meta = forcerFull ? null : await getCatalogueMeta(soc, mag);

                if (this.abortCtrl) this.abortCtrl.abort();
                this.abortCtrl = new AbortController();

                const res = await verifierAccesScanGo({
                    soc, mag,
                    key: this.keyParam,
                    deviceId: this.deviceId,
                    catalogueVersion: meta ? meta.version : null,
                    signal: this.abortCtrl.signal,
                });

                if (res.status === "denied") {
                    await this.purgerApresRefus(res.code);
                    return this.refuser(res.code);
                }

                if (res.status === "network") {
                    // Le wifi de salle tombe souvent : ce n'est pas une raison
                    // pour couper le serveur de ses commandes en cours.
                    const dispo = await getCatalogueMeta(soc, mag);
                    if (dispo) {
                        this.catalogueMeta = dispo;
                        await this.demarrerService("offline");
                        return;
                    }
                    return this.refuser("OFFLINE_NO_CACHE");
                }

                const ok = await this.appliquerReponse(res.data, forcerFull);
                if (ok) await this.demarrerService("ready");
            },

            /** Retourne false si un re-fetch complet a été relancé. */
            async appliquerReponse(data, dejaForce) {
                const soc = this.socParam, mag = this.magParam;
                const cat = data.catalogue || {};
                const meta = { version: cat.version, checksum: cat.checksum, serverNow: data.serverNow };

                if (cat.mode === "full") {
                    this.seedProgress = { fait: 0, total: (cat.upsert || []).length };
                    await saveCatalogue(soc, mag, cat.upsert || [], meta, (fait, total) => {
                        this.seedProgress = { fait, total };
                    });
                    this.seedProgress = null;
                } else if (cat.mode === "delta") {
                    await applyCatalogueDelta(soc, mag, cat, meta);
                } else {
                    const local = await getCatalogueMeta(soc, mag);
                    const sain = local && await verifyCatalogueIntegrity(soc, mag, local);
                    if (!sain && !dejaForce) {
                        // Un seul rattrapage, jamais de boucle.
                        await clearCatalogue(soc, mag);
                        await this.verifierEtCharger(true);
                        return false;
                    }
                    if (sain) await touchCatalogueMeta(soc, mag, data.serverNow);
                }

                await saveSession(soc, mag, {
                    key: this.keyParam,
                    serverNow: data.serverNow,
                    ttlSeconds: data.ttlSeconds,
                    quota: data.quota,
                    magLabel: data.magLabel,
                    socLabel: data.socLabel,
                });
                this.sessionMeta = await getSession(soc, mag);
                this.catalogueMeta = await getCatalogueMeta(soc, mag);
                return true;
            },

            /** Accès obtenu : on monte la carte, puis les commandes du service. */
            async demarrerService(etat) {
                // APRÈS l'accès, jamais avant : purger sur la foi de l'URL
                // effacerait le travail en cours d'un magasin légitime.
                await purgerScopesObsoletes(this.socParam, this.magParam);

                try {
                    const articles = await getAllArticles(this.socParam, this.magParam);
                    this.carte = catalogueVersCarte(articles);
                } catch (e) {
                    console.error("[srv] lecture du catalogue:", e);
                    this.carte = [];
                }
                // Sans attendre : la prise de commande ne dépend pas de cette liste.
                this.chargerIngredientsCarte();

                // L'identité doit être connue AVANT de charger les commandes : sur
                // un téléphone partagé, on n'expose que les commandes du serveur
                // courant (isolation par pid). D'où la lecture d'identité en premier.
                this.serveur = await this.chargerIdentite();

                await this.rechargerCommandes();

                // Filet anti-perte : si le store local est vide pour ce serveur mais
                // qu'une sauvegarde existe au backend (mise à jour PWA, changement de
                // téléphone), on la restaure. Réseau-first, silencieux si hors ligne.
                if (etat === "ready") await this.restaurerStoreDepuisBackend();

                await this.chargerTablesSalle();

                // Reprend les vérifications d'encaissement laissées en suspens
                // (appli fermée pendant un poll) - dans la limite des 3 essais.
                this.reprendreVerifications();

                this.bootState = etat;

                // Enrôlement temps réel présent → on ouvre le canal (bonus au QR).
                this.connecterTempsReel();
            },

            /** (Re)lit les commandes du serveur courant depuis le store local. */
            async rechargerCommandes() {
                try {
                    const pid = this.serveur ? this.serveur.pid : "";
                    const brutes = await getCommandes(this.socParam, this.magParam, pid);
                    this.commandes = brutes.map((c) => this.migrerCommande(c));
                } catch (e) {
                    console.error("[srv] lecture des commandes:", e);
                    this.commandes = [];
                }
            },

            /* ------------------------------------------- sauvegarde backend

               Le store d'un serveur (identité + commandes du jour) est poussé au
               backend, CHIFFRÉ, pour survivre à une perte du store local (mise à
               jour de la PWA, panne/changement de téléphone). La donnée suit le
               PID serveur : un collègue qui prête son téléphone ne mélange rien. */

            /** Libellé court de l'appareil, pour la présence (onglet Paramètres). */
            nomAppareil() {
                try {
                    const ua = navigator.userAgent || "";
                    const m = ua.match(/\(([^)]+)\)/);
                    let s = m ? m[1] : ua;
                    // On garde les 2 premiers segments (ex. « Linux; Android 12 »).
                    s = s.split(";").slice(0, 2).join(";").trim();
                    return s.slice(0, 80) || "Appareil";
                } catch (e) { return "Appareil"; }
            },

            /** Sauvegarde différée (anti-rafale) : regroupe les écritures rapprochées. */
            _planifierSauvegardeBackend() {
                if (!this.serveur || !this.serveur.pid) return;
                if (this._saveBackendTimer) clearTimeout(this._saveBackendTimer);
                this._saveBackendTimer = setTimeout(() => this.sauvegarderStoreBackend(), 3000);
            },

            /** Pousse la photo chiffrée du store au backend (best-effort, silencieux hors ligne). */
            async sauvegarderStoreBackend() {
                if (this._saveBackendTimer) { clearTimeout(this._saveBackendTimer); this._saveBackendTimer = null; }
                const pid = this.serveur ? this.serveur.pid : "";
                if (!pid || !this.keyParam) return;
                if (typeof navigator !== "undefined" && navigator.onLine === false) return;
                try {
                    const blob = await exporterStore(this.socParam, this.magParam, pid);
                    if (!blob) return;
                    await sauvegarderStoreServeur({
                        key: this.keyParam, serveurPid: pid, deviceId: this.deviceId, blob,
                    });
                } catch (e) {
                    console.error("[srv] sauvegarde store backend:", e);
                }
            },

            /**
             * Restaure le store depuis le backend UNIQUEMENT si le local est vide
             * pour ce serveur : ne jamais écraser un travail en cours par une photo
             * plus ancienne. Réhydrate puis relit la liste.
             */
            async restaurerStoreDepuisBackend() {
                const pid = this.serveur ? this.serveur.pid : "";
                if (!pid || !this.keyParam) return;
                if (this.commandes && this.commandes.length) return;   // local non vide → on ne touche pas
                if (typeof navigator !== "undefined" && navigator.onLine === false) return;
                try {
                    const res = await chargerStoreServeur({
                        key: this.keyParam, serveurPid: pid, deviceId: this.deviceId,
                    });
                    if (res.status !== "ok" || !res.blob) return;
                    const n = await importerStore(this.socParam, this.magParam, res.blob);
                    if (n > 0) {
                        await this.rechargerCommandes();
                        if (window.toastr) window.toastr.info("Commandes restaurées depuis le serveur.", "<b></b>");
                    }
                } catch (e) {
                    console.error("[srv] restauration store backend:", e);
                }
            },

            /**
             * Tables de salle - réseau-first, repli cache hors ligne.
             *
             * La liste (vide comprise) détermine le mode :
             *  - des tables → parcours normal avec choix de table ;
             *  - liste vide après un chargement réussi → l'établissement n'a pas
             *    de tables (comptoir) : mode sans table, on ira droit à la carte ;
             *  - réseau KO → on retombe sur le cache ; pas de cache → mode sans
             *    table par défaut, sans bloquer la prise de commande. Le prochain
             *    passage en ligne rechargera.
             */
            async chargerTablesSalle() {
                let cache = null;
                try {
                    cache = await getTables(this.keyParam);
                } catch (e) {
                    console.error("[srv] lecture cache tables:", e);
                }

                let res = { status: "network" };
                try {
                    res = await chargerTablesScanGo({ key: this.keyParam, deviceId: this.deviceId });
                } catch (e) {
                    console.error("[srv] chargement tables:", e);
                }

                if (res.status === "ok") {
                    this.tables = res.tables;
                    this.modeSansTable = res.tables.length === 0;
                    try { await saveTables(this.keyParam, res.tables); } catch (e) { /* cache best-effort */ }
                    return;
                }

                // Réseau indisponible (ou refus) : le cache fait foi s'il existe.
                if (Array.isArray(cache)) {
                    this.tables = cache;
                    this.modeSansTable = cache.length === 0;
                    return;
                }

                // Jamais chargées et hors ligne : ne pas bloquer le service.
                this.tables = [];
                this.modeSansTable = true;
            },

            /**
             * Ingrédients qu'un client peut faire retirer ou doubler, posés sur
             * chaque plat de la carte (`plat.ingredients`, lus par la fiche du plat).
             *
             * Le cache d'abord, pour que le choix soit proposé tout de suite et
             * hors ligne ; le réseau ensuite, qui fait foi. Une panne laisse la
             * liste en cache ; sans liste, les plats se commandent normalement.
             */
            async chargerIngredientsCarte() {
                const poser = (articles) => {
                    for (const plat of this.carte) plat.ingredients = (articles && articles[plat.pid]) || [];
                };

                try {
                    const cache = await getIngredients(this.keyParam);
                    if (cache) poser(cache);
                } catch (e) {
                    console.error("[srv] lecture cache ingrédients:", e);
                }

                try {
                    const res = await chargerIngredientsScanGo({ key: this.keyParam, deviceId: this.deviceId });
                    if (res.status !== "ok" || this.demonte) return;
                    poser(res.articles);
                    try { await saveIngredients(this.keyParam, res.articles); } catch (e) { /* cache best-effort */ }
                } catch (e) {
                    console.error("[srv] chargement ingrédients:", e);
                }
            },

            /* ---------------------------------------------------- refus */

            /**
             * Sur refus, on invalide l'ACCÈS - jamais le travail.
             *
             * Seule la session est effacée. Trois choses sont volontairement
             * épargnées :
             *
             *  - le catalogue, qui appartient aussi à /menu et /panier ;
             *  - les commandes du serveur. KEY_INVALID veut dire « le QR a été
             *    régénéré », pas « ces commandes sont fausses ». Les purger
             *    ferait perdre son service à chaque serveur dès qu'un manager
             *    réimprime l'affiche - des plats déjà servis, non facturés ;
             *  - l'identité : c'est la même personne, la lui redemander
             *    n'apporte rien.
             *
             * Le nettoyage des scopes réellement obsolètes est fait ailleurs,
             * par purgerScopesObsoletes(), une fois l'accès obtenu.
             */
            async purgerApresRefus(code) {
                try {
                    await clearSession(this.socParam, this.magParam);
                } catch (e) {
                    console.error("[srv] purge après refus:", e);
                }
            },

            refuser(code) {
                if (this.demonte) return;
                this.bootState = "denied";
                try {
                    // La clé fait 280 caractères et ne doit jamais transiter
                    // par une URL : la page de refus la relit ici.
                    sessionStorage.setItem("sg_retry_url", this.$route.fullPath);
                } catch (e) { /* mode privé : on perd juste le bouton Réessayer */ }
                this.$router.replace({ name: "panierrefus", query: { c: code, t: "serveur" } });
            },

            /* ------------------------------------------------ connexion */

            majConnexion() {
                this.horsLigne = !navigator.onLine;
                if (navigator.onLine && this.bootState === "offline") this.verifierEtCharger(false);
            },

            // Onglet resté ouvert des heures : on ne revérifie que si la
            // session a expiré entre-temps.
            onVisibility() {
                if (document.visibilityState !== "visible") return;
                if (this.bootState === "denied" || this.bootState === "checking") return;
                if (!isSessionFresh(this.sessionMeta)) this.verifierEtCharger(false);
            },

            /* ============================================================
               Temps réel (WebSocket) - pousse la commande à la caisse sans QR.
               BONUS : le QR reste la voie de secours, rien n'en dépend.
               ============================================================ */

            /**
             * Ouvre le canal si l'identité porte un enrôlement temps réel.
             * Le socket n'est pas réactif (this._sock), comme dans les modales caisse.
             */
            connecterTempsReel() {
                const rt = this.serveur && this.serveur.rt;
                if (!rt || !rt.room || !rt.token) return;   // pas d'enrôlement RT → mode QR

                this.rt.actif = true;
                this.rt.room = rt.room;
                this.rt.token = rt.token;
                this.rt.pidcaisse = rt.pidcaisse || "";

                if (this._sock) this._sock.deconnecter();
                this._sock = new ScangoSocket();
                this._sock.connecter(
                    {
                        room: rt.room, token: rt.token, role: "serveur",
                        // Métadonnées d'appareil pour la présence (onglet Paramètres).
                        deviceId: this.deviceId || "", deviceNom: this.nomAppareil(),
                    },
                    {
                        onConnect: () => { this.rt.socket = true; this._annulerReconnexionRt(); },
                        onRefus: (code) => {
                            this.rt.socket = false; this.rt.caisse = false;
                            /* ENROL_* = jeton révoqué (kick) / expiré / invalide :
                               fin du temps réel, on retombe en mode QR. */
                            if (String(code || "").indexOf("ENROL_") === 0) {
                                this.rt.actif = false;
                                if (window.toastr) window.toastr.info("Caisse temps réel indisponible - mode QR.", "<b></b>");
                                return;
                            }
                            /* Sinon (ex. « Invalid namespace » après un redémarrage
                               serveur) : le jeton reste valide, on retente jusqu'au
                               retour du canal - sans harceler. */
                            this._planifierReconnexionRt();
                        },
                        onCoupure: () => {
                            this.rt.socket = false; this.rt.caisse = false;
                            if (window.toastr) window.toastr.warning("Connexion caisse perdue - présentez le QR.", "<b></b>");
                        },
                    }
                );

                /* Présence : la caisse est-elle bien dans la room ? Le voyant ne
                   passe au vert que si NOTRE socket est up ET la caisse présente. */
                this._sock.sur("presence", (p) => {
                    this.rt.socket = true;
                    this.rt.caisse = !!(p && p.caisse);
                });
                this._sock.sur("ack", (d) => this.onAckTempsReel(d));
                this._sock.sur("notif", (d) => this.onNotifTempsReel(d));
                this._sock.sur("kicked", () => this.onKickTempsReel());
                /* Cuisine/bar : un plat de ce serveur est prêt à être servi. */
                this._sock.sur("plat_pret", (d) => this.onPlatPret(d));
                this._sock.sur("plat_vu_ack", (d) => this.onPlatVuAck(d));
            },

            /**
             * Pousse la commande à la caisse en temps réel. Même payload que le QR.
             * @returns {boolean} true si le message est parti (socket connecté).
             */
            emettreCommandeTempsReel(commande, mode) {
                if (!this._sock || !this._sock.connecte()) return false;
                const modeQr = mode === "service" ? "service" : "paiement";
                return this._sock.emettre("cmd", { txt: payloadCommande(commande, modeQr) });
            },

            /** Accusé de réception de la caisse : on l'affiche sur le QR courant. */
            onAckTempsReel(d) {
                const id = d && d.srvcmdid ? String(d.srvcmdid) : "";
                const statut = d && d.statut ? String(d.statut) : "recue";
                this.rtAck = { id, statut };
                if (window.toastr) {
                    const txt = statut === "chargee" ? "Commande chargée en caisse." : "Commande reçue par la caisse.";
                    window.toastr.success(txt, "<b></b>");
                }
                // La caisse a chargé la commande → l'envoi cuisine est acté : on
                // exécute automatiquement « Commande envoyée » (le serveur n'a plus
                // à le taper). Uniquement quand elle a bien été chargée.
                if (statut === "chargee") this._autoEnvoiSurChargement(id);
            },

            /**
             * Exécute « Commande envoyée » automatiquement pour la commande dont la
             * caisse vient de confirmer le chargement - si c'est bien celle affichée
             * en QR d'envoi cuisine. Aligne qteEnvoyee et referme le QR.
             */
            _autoEnvoiSurChargement(id) {
                const active = this.commandeActive;
                if (active && this.modeQr === "service" && this.vue === "qr"
                    && identifiantCommande(active) === String(id)) {
                    this.marquerEnvoye(active);
                }
            },

            /** La caisse a déconnecté ce serveur : fin du temps réel, retour au QR. */
            onKickTempsReel() {
                this.rt.actif = false; this.rt.socket = false; this.rt.caisse = false;
                if (window.toastr) window.toastr.warning("Déconnecté par la caisse - mode QR.", "<b></b>");
                this.deconnecterTempsReel();
            },

            /* Retente la connexion après un refus transitoire (namespace pas encore
               ré-enregistré au redémarrage serveur). Backoff léger, jamais en tempête. */
            _planifierReconnexionRt() {
                if (!this.rt.actif || this.demonte) return;
                if (this._rtRetryTimer) return;   // déjà planifié
                this._rtRetryTimer = setTimeout(() => {
                    this._rtRetryTimer = null;
                    if (this.rt.actif && this.serveur && !this.demonte) this.connecterTempsReel();
                }, 5000);
            },

            _annulerReconnexionRt() {
                if (this._rtRetryTimer) { clearTimeout(this._rtRetryTimer); this._rtRetryTimer = null; }
            },

            deconnecterTempsReel() {
                this._annulerReconnexionRt();
                if (this._sock) { this._sock.deconnecter(); this._sock = null; }
                this.rt.socket = false; this.rt.caisse = false;
            },

            /* ---- Filet de confirmation par poll (ack WS manqué OU mode QR pur) ----
               La caisse écrit le statut durable à l'ingestion quel que soit le
               transport : ce poll confirme donc « reçue par la caisse » même sans
               WebSocket. Borné, s'arrête dès qu'un ack (WS ou poll) est acquis. */
            planifierPollCommande(commande) {
                if (!commande) return;
                if (!this._cmdPollTimers) this._cmdPollTimers = {};
                const id = identifiantCommande(commande);
                if (this.rtAck && String(this.rtAck.id) === String(id)) return; // déjà confirmé
                this.annulerPollCommande(id);
                this._cmdPollTimers[id] = { essai: 0 };
                this._planifierProchainPollCommande(commande, id);
            },

            _planifierProchainPollCommande(commande, id) {
                const etat = this._cmdPollTimers && this._cmdPollTimers[id];
                if (!etat) return;
                const delais = [8000, 8000, 8000, 8000];   // ~32 s, 4 essais
                if (etat.essai >= delais.length) { this.annulerPollCommande(id); return; }
                etat.timer = setTimeout(() => this.executerPollCommande(commande, id), delais[etat.essai]);
            },

            async executerPollCommande(commande, id) {
                if (this.demonte) return;
                const etat = this._cmdPollTimers && this._cmdPollTimers[id];
                if (!etat) return;
                etat.essai += 1;

                if (this.rtAck && String(this.rtAck.id) === String(id)) { this.annulerPollCommande(id); return; }

                let statut = "";
                try {
                    const res = await statutCommandeScanGo({ key: this.keyParam, deviceId: this.deviceId, ids: [id] });
                    if (res.status === "ok") {
                        const row = (res.commandes || []).find((c) => String(c.srvcmdid) === String(id));
                        statut = row ? String(row.statut || "") : "";
                    }
                } catch (e) {
                    console.error("[srv] poll commande:", e);
                }

                // Filet : à chaque essai, on récupère aussi les événements caisse non-lus.
                this.verifierEvenementsNonLus(commande);

                if (statut === "chargee" || statut === "encaissee") {
                    this.rtAck = { id, statut: "chargee" };
                    if (window.toastr) window.toastr.success("Commande reçue par la caisse.", "<b></b>");
                    this._autoEnvoiSurChargement(id);
                    this.annulerPollCommande(id);
                    return;
                }
                this._planifierProchainPollCommande(commande, id);
            },

            annulerPollCommande(id) {
                if (this._cmdPollTimers && this._cmdPollTimers[id]) {
                    if (this._cmdPollTimers[id].timer) clearTimeout(this._cmdPollTimers[id].timer);
                    delete this._cmdPollTimers[id];
                }
            },

            annulerTousPollsCommande() {
                if (!this._cmdPollTimers) return;
                for (const k of Object.keys(this._cmdPollTimers)) {
                    if (this._cmdPollTimers[k].timer) clearTimeout(this._cmdPollTimers[k].timer);
                }
                this._cmdPollTimers = {};
            },

            /* ══════════ Événements de commande (retraits caisse, modifs) ══════════ */

            /** Prépare l'affichage des événements à l'ouverture d'une commande. */
            _ouvrirEvenements(commande) {
                this.evenementsActive = [];
                this.showHistoriqueEvenements = false;
                this.chargerEvenements(commande, false);
            },

            /** Charge le journal d'une commande. nonlus=false → historique complet. */
            async chargerEvenements(commande, nonlus = false) {
                if (!commande || !this.keyParam) return;
                const id = identifiantCommande(commande);
                try {
                    const res = await evenementsCommandeScanGo({
                        key: this.keyParam, deviceId: this.deviceId, ids: [id], nonlus,
                    });
                    if (res.status === "ok") this._appliquerEvenements(id, res.evenements || [], false);
                } catch (e) { console.error("[srv] evenements:", e); }
            },

            /** Récupère les non-lus (poll/WS) et notifie l'utilisateur. */
            async verifierEvenementsNonLus(commande) {
                if (!commande || !this.keyParam) return;
                const id = identifiantCommande(commande);
                try {
                    const res = await evenementsCommandeScanGo({
                        key: this.keyParam, deviceId: this.deviceId, ids: [id], nonlus: true,
                    });
                    if (res.status === "ok" && (res.evenements || []).length) {
                        this._appliquerEvenements(id, res.evenements, true);
                    }
                } catch (e) { /* silencieux */ }
            },

            /** Fusionne des événements dans evenementsActive (si c'est la commande affichée). */
            _appliquerEvenements(id, events, notifier) {
                const actifId = this.commandeActive ? identifiantCommande(this.commandeActive) : "";
                if (String(actifId) !== String(id)) return;
                const parId = new Map((this.evenementsActive || []).map((e) => [String(e.id), e]));
                for (const e of events) parId.set(String(e.id), e);
                this.evenementsActive = Array.from(parId.values()).sort((a, b) => (a.id || 0) - (b.id || 0));
                if (notifier && window.toastr) {
                    const dernier = events[events.length - 1];
                    if (dernier) window.toastr.info(dernier.message || "Mise à jour de la caisse", "<b></b>");
                }
            },

            /** Marque comme lus les événements affichés (après que le serveur les a vus). */
            async marquerEvenementsLus() {
                const ids = (this.evenementsActive || []).filter((e) => !e.lu_at).map((e) => e.id);
                if (!ids.length || !this.keyParam) return;
                try {
                    await marquerLuEvenementScanGo({ key: this.keyParam, deviceId: this.deviceId, ids });
                    const now = new Date().toISOString();
                    this.evenementsActive = this.evenementsActive.map((e) => (e.lu_at ? e : { ...e, lu_at: now }));
                } catch (e) { /* best-effort */ }
            },

            /** WS : un plat de ce serveur est prêt (émis par la cuisine/le bar). */
            onPlatPret(d) {
                if (!d || !d.bonId) return;
                const i = this.platsPrets.findIndex((x) => Number(x.bonId) === Number(d.bonId));
                if (i >= 0) this.platsPrets.splice(i, 1, d);
                else this.platsPrets.push(d);
                if (window.toastr) {
                    window.toastr.success(
                        (d.tableNom ? d.tableNom + ' - ' : '') + 'Plat prêt à servir' +
                        (d.numeroBon ? ' (' + d.numeroBon + ')' : ''),
                        "<b></b>"
                    );
                }
                /* Bip + vibration : le serveur est en salle, pas les yeux sur l'écran. */
                this.bipPret();
            },

            /** Le serveur acquitte : « je prends ». Pose vu_at côté backend. */
            acquitterPlat(p) {
                if (!p || !p.bonId) return;
                if (this._sock && this._sock.connecte()) {
                    this._sock.emettre("plat_vu", { bonId: p.bonId });
                }
                this.platsPrets = this.platsPrets.filter((x) => Number(x.bonId) !== Number(p.bonId));
            },

            onPlatVuAck() { /* confirmation silencieuse : le retrait local a déjà eu lieu */ },

            bipPret() {
                try {
                    if (navigator.vibrate) navigator.vibrate([120, 60, 120]);
                    const Ctx = window.AudioContext || window.webkitAudioContext;
                    if (!Ctx) return;
                    const ctx = new Ctx();
                    const osc = ctx.createOscillator(), gain = ctx.createGain();
                    osc.type = "sine"; osc.frequency.value = 980;
                    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
                    gain.gain.exponentialRampToValueAtTime(0.22, ctx.currentTime + 0.02);
                    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.3);
                    osc.connect(gain); gain.connect(ctx.destination);
                    osc.start(); osc.stop(ctx.currentTime + 0.31);
                    setTimeout(() => { try { ctx.close(); } catch (e) {} }, 500);
                } catch (e) { /* le son ne doit jamais casser l'app */ }
            },

            /** WS : notification poussée par la caisse (retrait, etc.). */
            onNotifTempsReel(d) {
                const id = d && d.srvcmdid ? String(d.srvcmdid) : "";
                if (!id) return;
                const active = this.commandeActive;
                if (active && String(identifiantCommande(active)) === id) {
                    this.verifierEvenementsNonLus(active);
                }
            },
        },
        async mounted() {
            // Timers de poll d'encaissement (non réactifs) : init AVANT bootstrap,
            // qui peut relancer des vérifications en suspens.
            this._pollTimers = {};
            this._cmdPollTimers = {};

            const p = this.$route.params || {};
            //this.socParam = p.soc || "";
            this.socParam = p.key || "";
            this.magParam = p.mag || "";
            this.keyParam = p.key || "";

            this.majConnexion();
            window.addEventListener("online", this.majConnexion);
            window.addEventListener("offline", this.majConnexion);
            document.addEventListener("visibilitychange", this.onVisibility);

            await this.bootstrap();
        },
        beforeUnmount() {
            // EN PREMIER : les callbacks async encore en vol ne doivent plus
            // naviguer une fois le composant démonté (refuser() teste ce drapeau).
            this.demonte = true;

            window.removeEventListener("online", this.majConnexion);
            window.removeEventListener("offline", this.majConnexion);
            document.removeEventListener("visibilitychange", this.onVisibility);
            if (this.abortCtrl) this.abortCtrl.abort();
            this.annulerTousTimers();
            this.annulerTousPollsCommande();
            if (this._saveBackendTimer) { clearTimeout(this._saveBackendTimer); this._saveBackendTimer = null; }
            this.deconnecterTempsReel();
            document.body.style.overflow = "";
        },
    };
</script>

<style>
    /* ══════════════════════════════════════════════════════════════════════
       Jetons de design - alignés sur la charte du Scan & Go (panier/index.vue).

       Le module serveur avait d'abord repris la maquette Stitch (sombre, ambre).
       On bascule sur la charte du panier : vert #0F6E56 sur fond clair. Les deux
       applications sont scannées dans le même magasin, souvent à quelques
       minutes d'intervalle - deux identités visuelles auraient donné le
       sentiment de deux produits différents.

       Le panier code ses couleurs en dur, sans variables. On les reprend ici
       sous forme de jetons parce que le module serveur est éclaté en onze
       composants : sans variables, un changement de teinte imposerait onze
       modifications. Les valeurs, elles, sont identiques au panier.

       Volontairement NON scopé : les écrans enfants doivent hériter de ces
       variables. Tout est préfixé --srv- pour ne pas entrer en collision avec
       le reste de l'application.
       ══════════════════════════════════════════════════════════════════════ */
    .srv-app {
        /* Fond et surfaces - clair, cartes blanches (panier .pnr-app) */
        --srv-bg: #f4f6f5;
        --srv-surface: #ffffff;
        --srv-surface-low: #ffffff;
        --srv-surface-high: #eef1f0;
        --srv-surface-highest: #e5e7eb;

        /* Texte */
        --srv-on: #14321f;
        --srv-on-variant: #5c6b64;
        --srv-outline: #9ca3af;
        --srv-outline-variant: #e5e7eb;
        --srv-border-fort: #d1d5db;

        /* Accent principal : le vert du Scan & Go */
        --srv-primary: #0F6E56;
        --srv-on-primary: #ffffff;
        --srv-primary-soft: #e6f4ef;

        /* États. Chaque couleur a sa teinte pâle de fond et sa teinte
           soutenue de texte - mêmes paires que les retours du panier
           (.fb-added, .fb-dup, .fb-notfound, .fb-miss). */
        --srv-success: #16a34a;
        --srv-success-soft: #dcfce7;
        --srv-warn: #b45309;
        --srv-warn-on: #92400e;
        --srv-warn-soft: #fef3c7;
        --srv-info: #1e40af;
        --srv-info-soft: #dbeafe;
        --srv-error: #b91c1c;
        --srv-error-soft: #fee2e2;

        /* Rythme : grille 4px */
        --srv-xs: 4px;
        --srv-sm: 8px;
        --srv-md: 16px;
        --srv-lg: 24px;
        --srv-xl: 32px;
        --srv-gutter: 16px;
        --srv-tap: 48px;

        /* Formes - celles du panier : 12px cartes et boutons, 8px champs,
           999px pastilles. L'ancienne charte utilisait 4px partout. */
        --srv-r: 12px;
        --srv-r-input: 8px;
        --srv-r-chip: 999px;
        --srv-r-full: 9999px;

        max-width: 480px;
        margin: 0 auto;
        min-height: 100vh;
        background: var(--srv-bg);
        color: var(--srv-on);
        font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
        -webkit-font-smoothing: antialiased;
        display: flex;
        flex-direction: column;
        position: relative;
    }

    /* Chiffres tabulaires : sans ça, les colonnes de montants dansent d'une
       ligne à l'autre et le serveur ne peut plus balayer une addition. */
    .srv-app .srv-num {
        font-variant-numeric: tabular-nums;
        font-feature-settings: "tnum";
    }

    /* ── Éléments partagés par les écrans ─────────────────────────────── */

    /* Volontairement NON collant.
       Le bandeau et l'en-tête vivent dans deux composants différents : on ne
       peut donc pas les empiler dans un même conteneur collant. Rendre les deux
       collants obligeait à décaler l'en-tête d'une hauteur de bandeau codée en
       dur - or ce bandeau passe sur deux lignes en écran étroit, et le décalage
       fixe produisait un recouvrement (le défaut même de la maquette Stitch,
       où « Bonjour, Jean » était tronqué).
       Le bandeau défile donc avec le contenu, et l'en-tête, lui, reste collé. */
    /* Bandeau hors ligne : ambre pâle sur brun, comme .pnr-offline. */
    .srv-offline {
        display: flex; align-items: center; gap: var(--srv-sm);
        padding: 8px var(--srv-gutter);
        background: var(--srv-warn-soft);
        color: var(--srv-warn-on);
        font-size: 12px; font-weight: 600; line-height: 1.4;
    }
    .srv-offline i { font-size: 15px; flex-shrink: 0; }

    /* Voyant temps réel : gris/ambre = pas de lien vivant (mode QR), vert = caisse
       joignable. Jamais figé vert sur une liaison morte (présence + socket up). */
    .srv-rt {
        display: flex; align-items: center; gap: var(--srv-sm);
        padding: 7px var(--srv-gutter);
        background: var(--srv-surface-high); color: var(--srv-on-variant);
        font-size: 12px; font-weight: 700; line-height: 1.4;
    }
    .srv-rt i { font-size: 15px; flex-shrink: 0; }
    .srv-rt-on { background: var(--srv-primary-soft); color: var(--srv-primary); }

    /* Plats prêts à servir (poussés par la cuisine/le bar) */
    .srv-prets { display: flex; flex-direction: column; gap: 6px; padding: 8px var(--srv-gutter); }
    .srv-pret-item {
        display: flex; align-items: center; gap: 10px;
        padding: 10px 12px; border-radius: 12px;
        background: var(--srv-primary-soft); border: 1px solid var(--srv-primary);
        animation: srv-pret-in .25s ease-out;
    }
    @keyframes srv-pret-in { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: none; } }
    .srv-pret-ico {
        width: 34px; height: 34px; flex: 0 0 34px; border-radius: 9px;
        display: inline-flex; align-items: center; justify-content: center;
        background: var(--srv-primary); color: #fff; font-size: 16px;
    }
    .srv-pret-info { flex: 1; display: flex; flex-direction: column; min-width: 0; }
    .srv-pret-info strong { font-size: 13px; color: var(--srv-primary); }
    .srv-pret-info span { font-size: 12px; color: var(--srv-on-variant); }
    .srv-pret-info em { font-style: normal; font-weight: 700; }
    .srv-pret-btn {
        flex-shrink: 0; border: none; cursor: pointer;
        background: var(--srv-primary); color: #fff; font-weight: 700; font-size: 13px;
        padding: 9px 14px; border-radius: 10px;
        display: inline-flex; align-items: center; gap: 5px;
    }

    /* En-tête vert plein, texte blanc - la signature visuelle du Scan & Go. */
    .srv-head {
        position: sticky; top: 0; z-index: 50;
        display: flex; align-items: center; gap: var(--srv-sm);
        padding: 12px 14px;
        background: var(--srv-primary);
        color: var(--srv-on-primary);
        min-height: 56px;
    }

    .srv-head-titre { font-size: 16px; font-weight: 600; color: var(--srv-on-primary); }
    .srv-head-sous  { font-size: 12px; font-weight: 500; color: rgba(255, 255, 255, .8);
                      margin-top: 1px; }
    .srv-head-sp    { flex: 1; }

    /* Sur fond vert, les icônes prennent la pastille translucide du panier
       (.pnr-newbtn) plutôt qu'un survol clair, illisible ici. */
    .srv-icone-btn {
        width: 40px; height: 40px;
        display: flex; align-items: center; justify-content: center;
        background: rgba(255, 255, 255, .18); border: none;
        color: var(--srv-on-primary);
        font-size: 20px; cursor: pointer; border-radius: 50%;
        flex-shrink: 0;
    }
    .srv-icone-btn:hover { background: rgba(255, 255, 255, .3); }

    /* Corps défilant : la barre d'onglets et les barres d'action sont fixes,
       il faut réserver leur hauteur sous peine de masquer la dernière ligne. */
    .srv-corps {
        flex: 1;
        padding: var(--srv-md) var(--srv-gutter);
        padding-bottom: 96px;
        overflow-y: auto;
    }

    .srv-btn {
        width: 100%; min-height: var(--srv-tap);
        display: flex; align-items: center; justify-content: center; gap: var(--srv-sm);
        padding: 0 var(--srv-md);
        background: var(--srv-primary); color: var(--srv-on-primary);
        border: none; border-radius: var(--srv-r);
        font-family: inherit; font-size: 15px; font-weight: 700;
        cursor: pointer;
    }
    /* Ombre portée verte du bouton principal (.pnr-shutter). */
    .srv-btn { box-shadow: 0 6px 16px rgba(15, 110, 86, .25); }
    .srv-btn:active { transform: scale(.98); }
    .srv-btn:disabled { background: #6b7280; box-shadow: none; opacity: .6; cursor: not-allowed; }
    .srv-btn-sec {
        background: var(--srv-surface); color: var(--srv-on);
        border: 1px solid var(--srv-border-fort); box-shadow: none;
    }
    .srv-btn-sec:disabled { background: var(--srv-surface-high); color: var(--srv-outline); }
    .srv-btn-danger {
        background: var(--srv-surface); color: var(--srv-error);
        border: 1px solid var(--srv-error); box-shadow: none;
    }

    .srv-carte {
        background: var(--srv-surface);
        border: 1px solid var(--srv-outline-variant);
        border-radius: var(--srv-r);
    }

    /* Pastilles d'état - paires fond pâle / texte soutenu du panier.
       L'ambre dit « à faire », le vert « réglé » : jamais l'inverse, c'est le
       seul repère couleur du serveur en salle. */
    .srv-pill {
        display: inline-flex; align-items: center; gap: 6px;
        padding: 4px 10px; border-radius: var(--srv-r-chip);
        font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;
        white-space: nowrap;
    }
    .srv-pill::before { content: ""; width: 7px; height: 7px; border-radius: 50%; background: currentColor; }
    .srv-pill-ouverte { background: var(--srv-surface-highest); color: var(--srv-on-variant); }
    .srv-pill-service { background: var(--srv-info-soft);    color: var(--srv-info); }
    .srv-pill-attente { background: var(--srv-warn-soft);    color: var(--srv-warn-on); }
    .srv-pill-verif   { background: var(--srv-info-soft);    color: var(--srv-info); }
    .srv-pill-ok      { background: var(--srv-success-soft); color: var(--srv-primary); }
    .srv-pill-moi     { background: var(--srv-primary-soft); color: var(--srv-primary); }

    /* Écran de vérification d'accès : plein vert, comme .pnr-boot. */
    .srv-boot {
        min-height: 100vh;
        display: flex; flex-direction: column;
        align-items: center; justify-content: center;
        gap: var(--srv-md); padding: var(--srv-lg);
        background: var(--srv-primary); color: var(--srv-on-primary);
        text-align: center;
    }
    .srv-boot-spin {
        width: 38px; height: 38px; border-radius: 50%;
        border: 3px solid rgba(255, 255, 255, .3);
        border-top-color: #fff;
        animation: srv-boot-rot .7s linear infinite;
    }
    @keyframes srv-boot-rot { to { transform: rotate(360deg); } }
    .srv-boot-txt { margin: 0; font-size: 14px; line-height: 1.6; opacity: .92; }
    .srv-boot-sub { margin: 0; font-size: 13px; opacity: .8; }

    .srv-vide {
        display: flex; flex-direction: column; align-items: center; justify-content: center;
        text-align: center; padding: 56px var(--srv-md); gap: var(--srv-sm);
        color: var(--srv-outline);
    }
    .srv-vide i { font-size: 44px; opacity: .6; }
    .srv-vide p { margin: 0; font-size: 15px; font-weight: 600; color: var(--srv-on-variant); }
    .srv-vide small { font-size: 13px; }

    /* Barre d'action basse, commune à la carte et au récapitulatif. */
    .srv-barre {
        position: fixed; bottom: 0; left: 50%; transform: translateX(-50%);
        width: 100%; max-width: 480px; z-index: 55;
        padding: var(--srv-sm) var(--srv-gutter) calc(var(--srv-sm) + env(safe-area-inset-bottom));
        background: var(--srv-surface);
        border-top: 1px solid var(--srv-outline-variant);
    }

    /* ── Onglets ──────────────────────────────────────────────────────── */

    .srv-tabs {
        position: fixed; bottom: 0; left: 50%; transform: translateX(-50%);
        width: 100%; max-width: 480px; z-index: 58;
        display: grid; grid-template-columns: repeat(4, 1fr);
        background: var(--srv-surface);
        border-top: 1px solid var(--srv-outline-variant);
        padding-bottom: env(safe-area-inset-bottom);
    }
    .srv-tab {
        min-height: 60px;
        display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 2px;
        background: none; border: none; cursor: pointer;
        color: var(--srv-outline); font-family: inherit;
        font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em;
    }
    .srv-tab i { font-size: 21px; }
    .srv-tab.active { color: var(--srv-primary); background: var(--srv-primary-soft); }

    @media (prefers-reduced-motion: reduce) {
        .srv-app *, .srv-app *::before, .srv-app *::after {
            animation-duration: .01ms !important; transition-duration: .01ms !important;
        }
    }

    /* ══ Bannière « modifications de la caisse » (retraits, rupture…) ══ */
    .srv-evt-banner {
        margin: 10px 12px 0; padding: 10px 12px; border-radius: 12px;
        background: var(--srv-surface); border: 1px solid var(--srv-outline-variant);
        border-left: 4px solid var(--srv-warn);
    }
    .srv-evt-banner.nonlu {
        background: var(--srv-warn-soft); border-color: var(--srv-warn);
    }
    .srv-evt-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
    .srv-evt-titre { font-weight: 800; font-size: 13px; color: var(--srv-warn-on); display: flex; align-items: center; gap: 6px; }
    .srv-evt-badge {
        background: var(--srv-error); color: #fff; border-radius: 50%;
        min-width: 18px; height: 18px; font-size: 11px; font-weight: 800;
        display: inline-flex; align-items: center; justify-content: center; padding: 0 4px;
    }
    .srv-evt-vu {
        border: 1px solid var(--srv-warn); background: transparent; color: var(--srv-warn-on);
        border-radius: 8px; padding: 4px 12px; font-size: 12px; font-weight: 700; cursor: pointer; font-family: inherit;
    }
    .srv-evt-list { list-style: none; margin: 8px 0 0; padding: 0; display: flex; flex-direction: column; gap: 4px; }
    .srv-evt-list li {
        display: flex; align-items: center; gap: 8px; font-size: 13px; color: var(--srv-on);
        padding: 4px 0;
    }
    .srv-evt-list li.nonlu { font-weight: 700; }
    .srv-evt-list li > i { color: var(--srv-error); flex-shrink: 0; }
    .srv-evt-msg { flex: 1; }
    .srv-evt-motif {
        font-size: 10px; text-transform: uppercase; font-weight: 800;
        background: var(--srv-error-soft); color: var(--srv-error);
        padding: 2px 6px; border-radius: 5px;
    }
    .srv-evt-hist {
        margin-top: 8px; background: transparent; border: none; color: var(--srv-primary);
        font-size: 12px; font-weight: 700; cursor: pointer; font-family: inherit;
        display: flex; align-items: center; gap: 5px; padding: 2px 0;
    }
    .srv-evt-all { border-top: 1px dashed var(--srv-outline-variant); margin-top: 8px; padding-top: 8px; }
    .srv-evt-all li { font-size: 12px; color: var(--srv-on-variant); gap: 6px; }
    .srv-evt-acteur {
        font-size: 10px; text-transform: uppercase; font-weight: 800; padding: 2px 6px; border-radius: 5px;
        background: var(--srv-surface-highest); color: var(--srv-on-variant);
    }
    .srv-evt-acteur.caisse { background: var(--srv-warn-soft); color: var(--srv-warn-on); }
    .srv-evt-acteur.serveur { background: var(--srv-primary-soft); color: var(--srv-primary); }
    .srv-evt-date { margin-left: auto; color: var(--srv-outline); font-size: 10px; }
</style>
